import {AbstractControl} from '@angular/forms';
import moment from 'moment';
import {
  MaskElement,
  MaskElementDirective,
  MaskValidatorConfig,
  MaskValidatorFunctions,
  MaskValidatorMethodMap,
  currencyArgs,
  currencyDate,
  currencyMail,
} from '../../../interfaces/mask';
import {MaskValidatorReturn} from '../../interfaces/mask-validator-return';
import {MaskDate} from '../mask-date/mask-date';

// @Injectable({
//   providedIn: 'root',
// })

export class MaskValidator {
  private control!: AbstractControl;
  private arrMasks!: MaskElementDirective[];
  private validateMask = false;

  static args: {[key: string]: unknown} = {};

  static date(args: currencyDate): MaskValidatorConfig<typeof MaskValidatorFunctions.DATE> {
    return {type: MaskValidatorFunctions.DATE, args};
  }

  static standard(): MaskValidatorConfig<typeof MaskValidatorFunctions.STANDARD> {
    return {type: MaskValidatorFunctions.STANDARD, args: {}};
  }

  static currency(args: currencyArgs): MaskValidatorConfig<typeof MaskValidatorFunctions.CURRENCY> {
    return {
      type: MaskValidatorFunctions.CURRENCY,
      args,
    };
  }

  static mail(mailRegex: RegExp): MaskValidatorConfig<typeof MaskValidatorFunctions.MAIL> {
    return {type: MaskValidatorFunctions.MAIL, args: {mailRegex}};
  }

  constructor(control: AbstractControl, arrMasks: MaskElementDirective[], validateMask = false) {
    this.control = control;
    this.arrMasks = arrMasks;
    this.validateMask = validateMask;
  }

  public call<T extends keyof MaskValidatorMethodMap>(fn: MaskValidatorConfig<T>): MaskValidatorReturn {
    const method = this[fn.type] as (args: MaskValidatorMethodMap[T]) => MaskValidatorReturn;
    return method.call(this, fn.args);
  }

  public standard(): MaskValidatorReturn {
    const ev = this.control.value;
    const arrMask = this.arrMasks as MaskElement[];
    const baseMaskObj: MaskValidatorReturn = {
      error: null,
      text: null,
      mask: null,
    };
    if (!ev) {
      return baseMaskObj;
    }

    const constructorObj: {string: string; text: string[]; mask: string[]} = {
      string: ev,
      text: [],
      mask: [],
    };
    let lastIndex = 0;
    let splitted = arrMask.reduce((obj, el, index) => {
      if (Array.isArray(el.mask)) {
        if (obj.string.length > el.mask.length) {
          lastIndex++;
        } else if (lastIndex === index) {
          obj.text = [];
          obj.mask = [];
          for (let i = 0; i < el.mask.length; i++) {
            const res = el.mask[i];
            if (obj.string[i]) {
              if (obj.string[i]?.match(res)) {
                obj.text.push(obj.string[i]);
              } else {
                lastIndex++;
                return obj;
              }
            } else if (el?.maskText?.[i]) {
              obj.mask.push(el.maskText[i]);
            }
          }
        }

        return obj;
      }

      if (obj.string.length > 0) {
        const split = obj.string.match(el.mask as RegExp);
        if (split && split.length > 0) {
          const splitEl = split.filter((f) => f?.length > 0);

          const txt: string = splitEl[1] || splitEl[0];
          obj.string = obj.string.replace(txt, '');
          obj.text.push(txt);
        } else {
          obj.string = '';
          obj.mask.push(el.maskText || (el.mask as string));
        }
      } else {
        obj.mask.push(el.maskText || (el.mask as string));
      }
      return obj;
    }, constructorObj);

    if (
      splitted.text.join('').length < (this.control as any).oldValue?.text?.join('').length &&
      ev.length > (this.control as any).oldValue?.text.join('').length
    ) {
      splitted = Object.assign({}, (this.control as any).oldValue);
    }

    baseMaskObj.text = splitted.text.join('');

    baseMaskObj.mask = splitted.mask.join('');

    // baseMaskObj.error = mailRegex.test(baseMaskObj.text)
    //   ? null
    //   : { email: true };

    if (baseMaskObj.text !== ev) {
      this.control.setValue(baseMaskObj.text, {emitEvent: false});
    }

    (this.control as any).oldValue = Object.assign({}, splitted);

    return baseMaskObj;
  }

  public currency(args: currencyArgs): MaskValidatorReturn {
    const control: AbstractControl & {oldValue: any} = this.control as AbstractControl & {oldValue: any};
    const {value} = control;

    const baseMaskObj: MaskValidatorReturn = {
      error: null,
      text: null,
      mask: null,
    };

    if (!value) {
      return baseMaskObj;
    }

    const currencyAsFloat = parseFloat(value.replace(/€ ([0-9\.]+)(,([0-9]+))?/, '$1.$30'));
    //check min / max
    if (MaskValidator.args['blur'] && args.min && args.min > currencyAsFloat) {
      baseMaskObj.error = {min: true};
      return baseMaskObj;
    }
    if (MaskValidator.args['blur'] && args.max && args.max < currencyAsFloat) {
      baseMaskObj.error = {max: true};
      return baseMaskObj;
    }

    const arrMask: MaskElement[] = this.arrMasks;

    const constructorObj: {string: string; text: string[]; mask: string[]} = {
      string: value,
      text: [],
      mask: [],
    };

    let splitted = arrMask.reduce((obj, el) => {
      if (obj.string.length > 0) {
        const split = obj.string.match(el.mask as RegExp);
        if (split && split.length > 0) {
          const splitEl = split.filter((f) => f?.length > 0);

          const txt: string = splitEl[1] || splitEl[0];
          obj.string = obj.string.replace(txt, '');
          obj.text.push(txt);
        } else {
          if (Array.isArray(el.mask) && typeof el.mask[0] === 'string') {
            obj.text.push(el.mask[0]);
          } else {
            obj.string = '';
            obj.mask.push(el.maskText || (el.mask as unknown as string));
          }
        }
      } else {
        obj.mask.push(el.maskText || (el.mask as string));
      }
      return obj;
    }, constructorObj);

    if (splitted.text.join('').length < control.oldValue?.text?.join('').length && value.length > control.oldValue?.text.join('').length) {
      splitted = Object.assign({}, control.oldValue);
    }

    baseMaskObj.text = splitted.text.join('');

    baseMaskObj.mask = splitted.mask.join('');

    if (MaskValidator.args['blur']) {
      baseMaskObj.text += baseMaskObj.mask;
      baseMaskObj.mask = '';
    }

    // baseMaskObj.error = mailRegex.test(baseMaskObj.text)
    //   ? null
    //   : { email: true };

    if (baseMaskObj.text !== value) {
      control.patchValue(baseMaskObj.text, {emitEvent: false});
    }

    control.oldValue = Object.assign({}, splitted);
    return baseMaskObj;
  }

  public date(args: currencyDate): MaskValidatorReturn {
    const ev = this.control.value;
    const dateLimits = args;

    if (!this.control?.value) {
      return {
        error: null,
        text: null,
        mask: null,
      };
    }

    const returnObj = new MaskDate().getControls(ev, this.arrMasks[0], dateLimits, args.isDeleting);

    const maskArrStr = this.arrMasks[0].mask.join('');
    const momentDate = moment(returnObj.text, maskArrStr);
    if (returnObj.text?.length === maskArrStr.length && momentDate.isValid()) {
      if (dateLimits.min && momentDate.isBefore(dateLimits.min)) {
        returnObj.error = {minDate: true};
      }

      if (dateLimits.max && momentDate.isAfter(dateLimits.max)) {
        returnObj.error = {maxDate: true};
      }
    } else {
      returnObj.error = {invalidDate: true};
    }

    if (returnObj.text !== ev) {
      this.control.patchValue(returnObj.text, {
        emitEvent: false,
        onlySelf: true,
      });
    }
    return returnObj;
  }

  public mail({mailRegex}: currencyMail): MaskValidatorReturn {
    const ev = this.control.value;
    const arrMask = this.arrMasks as MaskElement[];
    const baseMaskObj: MaskValidatorReturn = {
      error: null,
      text: null,
      mask: null,
    };
    if (!ev) {
      return baseMaskObj;
    }

    const constructorObj: {string: string; text: string[]; mask: string[]} = {
      string: ev,
      text: [],
      mask: [],
    };
    let splitted = arrMask.reduce((obj, el) => {
      if (obj.string.length > 0) {
        const split = obj.string.match(el.mask as RegExp);
        if (split && split.length > 0) {
          const splitEl = split.filter((f) => f?.length > 0);

          const txt: string = splitEl[1] || splitEl[0];
          obj.string = obj.string.replace(txt, '');
          obj.text.push(txt);
        } else {
          obj.string = '';
          obj.mask.push(el.maskText || (el.mask as string));
        }
      } else {
        obj.mask.push(el.maskText || (el.mask as string));
      }
      return obj;
    }, constructorObj);

    if (
      splitted.text.join('').length < (this.control as any).oldValue?.text?.join('').length &&
      ev.length > (this.control as any).oldValue?.text.join('').length
    ) {
      splitted = Object.assign({}, (this.control as any).oldValue);
    }

    baseMaskObj.text = splitted.text.join('');

    baseMaskObj.mask = splitted.mask.join('');

    baseMaskObj.error = mailRegex.test(baseMaskObj.text) ? null : {email: true};

    if (baseMaskObj.text !== ev) {
      this.control.setValue(baseMaskObj.text, {emitEvent: false});
    }

    (this.control as any).oldValue = Object.assign({}, splitted);

    return baseMaskObj;
  }
}
